import React, { useState, useEffect, useRef } from "react";
import { Switch, Divider, Select, Button, Popconfirm, Segmented, Tooltip } from "antd";
import { MobileOutlined, TabletOutlined, LaptopOutlined, ArrowUpOutlined, ArrowDownOutlined, CheckCircleOutlined, PauseCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import css from "./ClientBuilderPanel.module.css";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { LANGUAGE_SELECT_OPTIONS } from "./translatable.util";
import { CardsEditor } from "./CardsEditor";
import { PaymentMethodTextsEditor } from "./PaymentMethodTextsEditor";
import { SchemaFieldControl } from "./SchemaFieldControl";
import { COMPONENT_REGISTRY, getLayoutOptions, buildDefaultEntry, mergeEntry, readEntryProps, writeEntryProp } from "./componentRegistry";

const brandCode = process.env.REACT_APP_PROJECT_CODE || "CK";

// Mobile/Tablet/Laptop preview-frame sizes — applies to every component's
// Preview uniformly (a plain width-constrained wrapper around whatever that
// Preview already renders at `width: "100%"`). Most Preview components have
// no real responsive/breakpoint logic of their own (plain inline styles, no
// media queries — see structuralPreviews.js), so this is a container resize
// only: it shows how content wraps/overflows in a narrower space, not a
// pixel-accurate reproduction of the live site's real mobile layout.
// StructuralNavbarPreview is the one exception with its own extra behavior
// (hiding nav links at "small", matching the real `hidden md:flex`
// breakpoint) — see its own comment in structuralPreviews.js.
const PREVIEW_FRAME_WIDTH_BY_SIZE = { small: 375, medium: 768, large: 1280 };
const PREVIEW_FRAME_DEVICE_LABEL = { small: "Mobile", medium: "Tablet", large: "Laptop" };

// Registry keys that only ever belong to the "Client dashboard" tab — kept
// in one place so the "Client site" tab's picker-exclusion filter and
// BuilderComponent.js's `filterKeys` prop (which whitelists exactly these
// same keys for the dashboard tab) can't drift out of sync as more
// dashboard-only entries are added later.
export const DASHBOARD_ONLY_KEYS = ["dashboardTheme", "dashboardPagesTheme"];

// Component-scoped draft key — mirrors the client-site propsMap so a page
// refresh mid-edit doesn't lose unpublished changes. Scoped per brand since
// each brand's build only ever talks to its own backend/config, AND per
// panel instance (`filterKeys`, joined, or "all" for the unfiltered "Client
// site" tab) so the "Client dashboard" tab's draft never collides with or
// overwrites the "Client site" tab's draft — each ClientBuilderPanel
// instance stores its own separate propsMap shape (Object.keys differ), so a
// shared key would let one instance's mount overwrite the other's draft.
function buildClientDraftKey(filterKeys) {
    return `builderDraft_client_${brandCode}_${filterKeys ? filterKeys.join(",") : "all"}`;
}

// Hero's `features`/`featuresColumns`/`featuresIconPosition` fields stay in
// the shared HERO_SCHEMA for E4F/CK (their real hero design nests this
// content inside the hero card) but MEELZ's real "features" live in a
// completely separate, always-live section — see the `featuresGrid`
// registry entry. Hidden here specifically for MEELZ's hero LAYOUT (not
// this admin build's brandCode — an admin can preview/edit any brand's
// hero layout regardless of which brand this dashboard is built for, so
// gating on brandCode hid these fields for E4F/CK layouts too whenever this
// admin happens to be a MEELZ build) so admins aren't shown fields with no
// corresponding live content only when MEELZ's own layout is selected.
const HERO_MEELZ_HIDDEN_FIELDS = new Set(["features", "featuresColumns", "featuresIconPosition"]);

// `filterKeys`, when provided, restricts this panel to only those
// COMPONENT_REGISTRY keys — used by the "Client dashboard" tab
// (BuilderComponent.js) to reuse this exact picker+form+preview UI scoped to
// just `dashboardTheme`, instead of building a second parallel panel
// component. Omitted (default `null`) for the existing "Client site" tab,
// which keeps showing every registry entry (minus the featuresGrid/
// dashboardTheme picker exclusions below) exactly as before this prop existed.
function ClientBuilderPanel({ value, onChange, publishedAt, onSelectedKeyChange, filterKeys = null }) {
    const registryEntries = filterKeys
        ? Object.values(COMPONENT_REGISTRY).filter((c) => filterKeys.includes(c.key))
        : Object.values(COMPONENT_REGISTRY);

    const buildDefaultPropsMap = () =>
        Object.fromEntries(registryEntries.map((c) => [c.key, buildDefaultEntry(c)]));

    const [selectedKey, setSelectedKey] = useState(registryEntries[0]?.key || "navbar");

    // Report the currently selected component up to the parent — lets
    // BuilderComponent's header-level History button (Client site tab)
    // target whichever component is currently selected, same as Admin's
    // header History button targets ADMIN_KEY.
    useEffect(() => {
        if (onSelectedKeyChange) onSelectedKeyChange(selectedKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey]);
    // Which language the admin is currently viewing/editing translatable
    // fields in — single-select, defaults to English. Purely local UI state,
    // not persisted as part of propsMap; switching languages only changes
    // which language's value is shown/edited, it never touches the backend.
    const [selectedLanguage, setSelectedLanguage] = useState("en");

    // Mobile/Tablet/Laptop preview-frame size, shared across every
    // component's Preview (see PREVIEW_FRAME_WIDTH_BY_SIZE above). Purely
    // local UI state, not persisted; resets to "large" on refresh like
    // selectedLanguage above.
    const [previewFrameSize, setPreviewFrameSize] = useState("large");

    // Per-session-only draft: mirrors propsMap to localStorage while the
    // admin is actively editing, purely so in-progress work survives a
    // React remount within the SAME visit (e.g. switching away from and
    // back to a component). A genuine page refresh always discards it —
    // see the mount effect below — so unpublished edits never leak into a
    // later visit and get mistaken for the current published state. Publish
    // is the only way to make an edit durable; refresh is not a save.
    const [draft, setDraft] = useLocalStorage(buildClientDraftKey(filterKeys), null);

    // Unconditionally clear any leftover draft on mount (component mount,
    // which for this panel means "the Builder page was opened/refreshed").
    // Anything still here is from a PRIOR visit that never published —
    // exactly the "changes not published, refresh happened" case this must
    // discard, not restore. useLocalStorage isn't the source of truth after
    // this; propsMap's own initial state below never reads `draft` for that
    // same reason.
    useEffect(() => {
        setDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [propsMap, setPropsMap] = useState(() => {
        if (value && Object.keys(value).length > 0) {
            return Object.fromEntries(
                registryEntries.map((c) => [c.key, mergeEntry(c, buildDefaultEntry(c), value[c.key])])
            );
        }
        return buildDefaultPropsMap();
    });

    // Every value change is a deliberate parent action — the initial
    // backend fetch, or a later one like handleRestoreSnapshot's
    // setClientComponents() — and must always win over whatever's currently
    // in propsMap; otherwise "Restore to editor" would silently no-op.
    const hasConsumedInitialValueRef = useRef(false);

    // Set to true only by a genuine user-initiated write (setProp,
    // setLayout, setActive, etc. below) — never by the initial
    // backend-fetch merge effect. Gates the draft-mirror effect so opening
    // the Builder with no real pending edits never persists a draft at all,
    // which in turn means the NEXT mount has nothing to wrongly "win" over
    // a fresh backend fetch. Without this, every mount silently wrote the
    // just-loaded backend state into the draft, so a later mount would load
    // that stale draft instead of re-fetching the (possibly since-changed)
    // real published state — showing stale content even though the admin
    // never actually edited anything in the prior session.
    const hasRealEditRef = useRef(false);

    // Every setProp/setTranslatableProp/etc. keystroke calls onChange(next)
    // synchronously, which is the parent's setClientComponents — that write
    // comes straight back down as this exact same `value` prop on the next
    // render. Without this guard, that echo would re-trigger the merge
    // effect below on every single keystroke (its dependency is
    // JSON.stringify(value)), which calls setPropsMap again with a fresh
    // mergeEntry-rebuilt object, fighting the state the keystroke itself
    // just set — this was the "Maximum update depth" / dropped-keystroke
    // crash under fast typing. Only a value the parent produced for a
    // reason OTHER than mirroring our own last emission (e.g. the initial
    // backend fetch, or handleRestoreSnapshot) should re-merge.
    const lastEmittedValueRef = useRef(null);

    // When parent loads saved clientConfig from DB, merge it in.
    useEffect(() => {
        const isInitialValue = !hasConsumedInitialValueRef.current;
        hasConsumedInitialValueRef.current = true;
        if (!value || Object.keys(value).length === 0) return;
        if (lastEmittedValueRef.current !== null && JSON.stringify(value) === lastEmittedValueRef.current) return;
        // A non-initial value change is a deliberate parent action (e.g.
        // handleRestoreSnapshot) carrying genuine unpublished work that must
        // survive an in-session remount — mark it as a real edit so the
        // draft-mirror effect persists it for that (see hasRealEditRef's
        // comment above; a real page refresh discards it regardless, by
        // design). The initial mount's own backend fetch must NOT set this
        // flag — it's just re-displaying the backend's own data, which needs
        // no draft protection.
        if (!isInitialValue) hasRealEditRef.current = true;
        setPropsMap(
            Object.fromEntries(
                registryEntries.map((c) => [c.key, mergeEntry(c, buildDefaultEntry(c), value[c.key])])
            )
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(value)]);

    // Mirror propsMap to the draft — but only once a real user edit has
    // happened (hasRealEditRef), so a mount with no pending changes never
    // creates a draft the NEXT mount could wrongly treat as "unpublished
    // work in progress" (see hasRealEditRef's comment above).
    useEffect(() => {
        if (!hasRealEditRef.current) return;
        setDraft(propsMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propsMap]);

    // Clear the draft once Publish succeeds — the backend now holds this
    // state, so there is nothing left to protect against a refresh.
    useEffect(() => {
        if (publishedAt == null) return;
        setDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publishedAt]);

    // Notifies the parent AFTER propsMap actually commits — never during
    // setPropsMap's own updater function. Every write path below used to
    // call onChange(next) synchronously INSIDE its setPropsMap((prev) => {...})
    // callback — calling another component's setState (BuilderComponent's
    // setClientComponents) from inside your own state-computation callback is
    // a render-phase side effect, which React explicitly warns against
    // ("Cannot update a component while rendering a different component") and
    // which — combined with the merge effect below reacting to that same
    // update — produced the "Maximum update depth exceeded" crash under fast
    // typing. Doing it here, in a useEffect keyed on propsMap, defers the
    // parent notification to a proper commit-phase effect instead.
    // lastEmittedValueRef still records what was just sent so the merge
    // effect below can recognize its own echo coming back down as `value`
    // and skip re-merging it.
    useEffect(() => {
        if (!onChange) return;
        lastEmittedValueRef.current = JSON.stringify(propsMap);
        onChange(propsMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propsMap]);

    // Generic write path (strategy-map pattern): layout-capable entries write
    // into byLayout[currentLayout][key]; flat entries write [key] directly.
    // The layouts-vs-flat decision itself lives in writeEntryProp (registry
    // module) — every writer below just supplies the entry/current/key/val.
    const setProp = (key, val) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const entry = COMPONENT_REGISTRY[selectedKey];
            // A stale localStorage draft saved before this component existed
            // in COMPONENT_REGISTRY won't have an entry for it yet — fall
            // back to a fresh default rather than reading off undefined.
            const current = prev[selectedKey] || buildDefaultEntry(entry);
            return { ...prev, [selectedKey]: writeEntryProp(entry, current, key, val) };
        });
    };

    // Writes into featuresGrid's own propsMap entry (NOT selectedKey's,
    // which is "hero" while this is used — see the below-hero edit block in
    // the props panel). featuresGrid was removed from the pickable
    // components list, but its data/registry entry still exists — this is
    // how admins edit its icon/label content now, via hero's own edit view.
    const setFeaturesGridItems = (items) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const entry = COMPONENT_REGISTRY.featuresGrid;
            const current = prev.featuresGrid || buildDefaultEntry(entry);
            return { ...prev, featuresGrid: writeEntryProp(entry, current, "items", items) };
        });
    };

    // Same generic write path as setProp, but for `translatable` top-level
    // schema fields: writes into value[selectedLanguage] instead of
    // replacing the whole field. A legacy flat-string value (pre-existing
    // published rows, saved before this field was translatable) is upgraded
    // to {en: <existingValue>} on first edit — never dropped.
    const setTranslatableProp = (key, val) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const entry = COMPONENT_REGISTRY[selectedKey];
            // Same stale-draft fallback as setProp above.
            const current = prev[selectedKey] || buildDefaultEntry(entry);
            const existing = readEntryProps(entry, current)[key];
            const asObject = existing == null || typeof existing === "string"
                ? (existing ? { en: existing } : {})
                : existing;
            const nextValue = { ...asObject, [selectedLanguage]: val };
            return { ...prev, [selectedKey]: writeEntryProp(entry, current, key, nextValue) };
        });
    };

    // Generic layout-select / active-toggle writers — only meaningful when
    // the selected entry has a `layouts` map.
    const setLayout = (layoutId) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const current = prev[selectedKey] || buildDefaultEntry(COMPONENT_REGISTRY[selectedKey]);
            const next = { ...prev, [selectedKey]: { ...current, layout: layoutId } };
            return next;
        });
    };

    const setActive = (isActive) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const current = prev[selectedKey] || buildDefaultEntry(COMPONENT_REGISTRY[selectedKey]);
            const next = { ...prev, [selectedKey]: { ...current, active: isActive } };
            return next;
        });
    };

    // Whole-section show/hide on the live client site — independent of
    // `active` (which only chooses published-layout vs. hardcoded-fallback
    // content, never hides anything). Same shape as setActive.
    const setVisible = (isVisible) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const current = prev[selectedKey] || buildDefaultEntry(COMPONENT_REGISTRY[selectedKey]);
            const next = { ...prev, [selectedKey]: { ...current, visible: isVisible } };
            return next;
        });
    };

    // Same active/visible writers as setActive/setVisible above, but for an
    // ARBITRARY registry key rather than always `selectedKey` — used by the
    // "Layout Order" panel's per-row Active/Shown toggles (below), which
    // let an admin flip another section's active/visible state without
    // leaving the reorder list to go select that section directly.
    const setActiveForKey = (key, isActive) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const current = prev[key] || buildDefaultEntry(COMPONENT_REGISTRY[key]);
            return { ...prev, [key]: { ...current, active: isActive } };
        });
    };
    const setVisibleForKey = (key, isVisible) => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const current = prev[key] || buildDefaultEntry(COMPONENT_REGISTRY[key]);
            return { ...prev, [key]: { ...current, visible: isVisible } };
        });
    };

    // Resets the CURRENTLY SELECTED component's local draft back to its true
    // schema default (buildDefaultEntry) — clears whatever stale/placeholder
    // data (e.g. leftover "New Link" cards from an earlier session, or old
    // content the layout's real defaults have since been corrected past)
    // is stuck in this component's slice of propsMap/localStorage draft.
    // Does NOT touch the backend — same "must Publish to persist" contract
    // as every other edit in this panel. Other components' data is untouched.
    const resetSelectedToDefault = () => {
        hasRealEditRef.current = true;
        setPropsMap((prev) => {
            const next = { ...prev, [selectedKey]: buildDefaultEntry(COMPONENT_REGISTRY[selectedKey]) };
            return next;
        });
    };

    const selected     = COMPONENT_REGISTRY[selectedKey];
    const selectedData = propsMap[selectedKey] || {};
    const props        = selected?.layouts ? readEntryProps(selected, selectedData) : selectedData;
    const Preview       = selected ? selected.Preview : null;

    return (
        <div className={css.root}>
            {/* Left: component list + prop controls */}
            <div className={css.leftPanel}>
                <div className={css.componentList}>
                    <div className={css.sectionLabel}>Components</div>
                    {/* featuresGrid/menuSelection stay real COMPONENT_REGISTRY
                        entries (their data still exists, still publishes/
                        saves/history's normally) — they're excluded ONLY from
                        this pickable list. MEELZ's featuresGrid content is
                        edited implicitly via the hero preview's composed
                        below-hero block instead (see previewCard below);
                        menuSelection has no Builder-editable content at all
                        (see its own componentRegistry.js comment) — both are
                        reorderable from the Layout Order panel below but
                        never independently selectable here.
                        DASHBOARD_ONLY_KEYS (dashboardTheme, dashboardPagesTheme)
                        are excluded the same way, but ONLY when this panel is
                        unfiltered (the "Client site" tab, filterKeys === null)
                        — they live in this shared registry purely for their
                        generic buildDefaultEntry/mergeEntry/publish/history
                        machinery there. When filterKeys IS set (the dedicated
                        "Client dashboard" tab's own ClientBuilderPanel
                        instance, see BuilderComponent.js), registryEntries is
                        already restricted to exactly the dashboard-only keys
                        by the filterKeys logic above — excluding them again
                        here would leave the picker empty, which is the bug
                        this condition fixes. */}
                    {registryEntries
                        .filter((c) => c.key !== "featuresGrid" && c.key !== "menuSelection" && c.key !== "sectionOrder" && (filterKeys || !DASHBOARD_ONLY_KEYS.includes(c.key)))
                        .map((c) => (
                        <button
                            key={c.key}
                            className={`${css.componentCard} ${selectedKey === c.key ? css.componentCardActive : ""}`}
                            onClick={() => setSelectedKey(c.key)}
                        >
                            <span className={css.componentName}>{c.label}</span>
                        </button>
                    ))}
                    {/* "Layout Order" is pulled out of the normal alphabetical/
                        registration-order flow above and pinned last, behind
                        its own divider — per real client feedback (2026-09-16):
                        the client testing the feature found it confusing
                        sitting in the middle of the regular component list.
                        Only rendered when it's actually part of this panel's
                        registryEntries (excluded entirely from the dedicated
                        "Client dashboard" tab via filterKeys, same as every
                        other client-site-only entry). */}
                    {registryEntries.some((c) => c.key === "sectionOrder") && (
                        <>
                            {/* Deliberately a plain thick bar, not the standard
                                thin <Divider> used everywhere else in this panel
                                — per client feedback (2026-09-16), the regular
                                hairline divider didn't stand out enough to mark
                                "Layout Order" as a distinct, separate section of
                                the list rather than just another list item. */}
                            <div style={{ height: 2, background: "#d0d0d0", margin: "8px 12px" }} />
                            <button
                                className={`${css.componentCard} ${selectedKey === "sectionOrder" ? css.componentCardActive : ""}`}
                                onClick={() => setSelectedKey("sectionOrder")}
                            >
                                {/* Always bold, not just when selected — per client
                                    feedback (2026-09-16), "Layout Order" needs to read
                                    as a distinct, standout item, not just another
                                    entry that happens to look the same as the rest
                                    until clicked. */}
                                <span className={css.componentName} style={{ fontWeight: 700 }}>{COMPONENT_REGISTRY.sectionOrder.label}</span>
                            </button>
                        </>
                    )}
                </div>

                <Divider style={{ margin: 0 }} />

                <div className={css.propControls}>
                    {selected && (
                        <>
                            {selected.layouts && Object.keys(selected.layouts).length > 1 && (
                                <div className={css.propRow}>
                                    <span className={css.propLabel}>Layout</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <Select
                                            value={selectedData.layout}
                                            onChange={setLayout}
                                            options={getLayoutOptions(selected)}
                                            size="small"
                                            style={{ width: 150 }}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className={css.sectionLabel} style={{ margin: "0 0 8px" }}>Props — {selected.label}</div>
                            {selected.schema
                                .filter(({ key }) => !(
                                    selectedKey === "hero" && selectedData.layout === "meelz" && HERO_MEELZ_HIDDEN_FIELDS.has(key)
                                ))
                                .map((field) => {
                                const { key, type } = field;
                                // A plain in-list divider/heading (e.g. quiz's "Contact
                                // Details" — otherwise contactStepTitle/contactFullNameLabel/
                                // etc. blend invisibly into the long flat field list with no
                                // visual break from the questions section above them).
                                // Reuses the same sectionLabel style as the panel's own
                                // "Props — {label}" heading above, just inline in the list.
                                if (type === "sectionHeader") {
                                    return (
                                        <div key={key}>
                                            <Divider style={{ margin: 0 }} />
                                            <div className={css.sectionLabel}>{field.label}</div>
                                        </div>
                                    );
                                }
                                // sectionOrder's `order` field is the one schema field that
                                // needs cross-entry access (another registry entry's own
                                // active/visible, not just this entry's own props) —
                                // SchemaFieldControl has no propsMap to read/write those from,
                                // so (unlike every other field type) this is handled entirely
                                // inline here instead of delegating to it. Reordering itself
                                // (move up/down) still writes through the normal setProp path.
                                if (type === "sectionOrderList") {
                                    const order = props[key] || [];
                                    const move = (index, delta) => {
                                        const target = index + delta;
                                        if (target < 0 || target >= order.length) return;
                                        const next = [...order];
                                        [next[index], next[target]] = [next[target], next[index]];
                                        setProp(key, next);
                                    };
                                    return (
                                        <div key={key} style={{ padding: "8px 0 4px" }}>
                                            <span className={css.propLabel} style={{ display: "block", padding: "0 16px 6px" }}>{field.label}</span>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 16px" }}>
                                                {order.map((itemKey, index) => {
                                                    const optionLabel = field.options?.find((o) => o.value === itemKey)?.label || itemKey;
                                                    const itemEntry = COMPONENT_REGISTRY[itemKey];
                                                    const itemData = propsMap[itemKey] || buildDefaultEntry(itemEntry);
                                                    return (
                                                        <div
                                                            key={itemKey}
                                                            style={{
                                                                display: "flex", flexDirection: "column", gap: 6,
                                                                border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px",
                                                                background: "#fff",
                                                            }}
                                                        >
                                                            {/* Label gets its own full-width line — the previous
                                                                single-line layout squeezed badge+label+switches+
                                                                arrows into one row, truncating every label to
                                                                "Her…"/"Our …"/etc. on a normal-width panel (confirmed
                                                                via live screenshot, direct client feedback
                                                                2026-09-16). Stacking guarantees the full name is
                                                                always readable regardless of panel width. */}
                                                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                                                <span
                                                                    style={{
                                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                                        width: 20, height: 20, borderRadius: "50%", background: "#f0f0f0",
                                                                        fontSize: 11, fontWeight: 600, color: "#666", flexShrink: 0,
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </span>
                                                                <span style={{ fontSize: 13, fontWeight: 500 }}>
                                                                    {optionLabel}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                                                                {/* reorderOnly entries (currently just menuSelection —
                                                                    see componentRegistry.js's own comment) have no
                                                                    Builder content or alternate version to toggle
                                                                    between, so they get position control only, no
                                                                    Active/Shown switches. Icon-only switches (no text
                                                                    children) + tooltips keep each row compact — the
                                                                    original text-labeled switches ("Active"/"Inactive",
                                                                    "Shown"/"Hidden") made a 5-row list feel cluttered,
                                                                    per direct client feedback (2026-09-16). */}
                                                                {!itemEntry?.reorderOnly && (
                                                                    <>
                                                                        <Tooltip title={itemData.active ? "Active, click to deactivate" : "Inactive, click to activate"}>
                                                                            <Switch
                                                                                checked={!!itemData.active}
                                                                                onChange={(v) => setActiveForKey(itemKey, v)}
                                                                                size="small"
                                                                                checkedChildren={<CheckCircleOutlined />}
                                                                                unCheckedChildren={<PauseCircleOutlined />}
                                                                            />
                                                                        </Tooltip>
                                                                        <Tooltip title={itemData.visible !== false ? "Shown, click to hide" : "Hidden, click to show"}>
                                                                            <Switch
                                                                                checked={itemData.visible !== false}
                                                                                onChange={(v) => setVisibleForKey(itemKey, v)}
                                                                                size="small"
                                                                                checkedChildren={<EyeOutlined />}
                                                                                unCheckedChildren={<EyeInvisibleOutlined />}
                                                                            />
                                                                        </Tooltip>
                                                                        <Divider type="vertical" style={{ margin: "0 2px", height: 18 }} />
                                                                    </>
                                                                )}
                                                                <Tooltip title="Move up">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<ArrowUpOutlined />}
                                                                        disabled={index === 0}
                                                                        onClick={() => move(index, -1)}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Move down">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<ArrowDownOutlined />}
                                                                        disabled={index === order.length - 1}
                                                                        onClick={() => move(index, 1)}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                }
                                // paymentMethodTexts has no SchemaFieldControl branch (it's
                                // a fixed-4-row editor, not one of the generic field types
                                // that component handles) — kept inline, same as before.
                                if (type === "paymentMethodTexts") {
                                    return (
                                        <div key={key} style={{ padding: "8px 0 4px" }}>
                                            <span className={css.propLabel} style={{ display: "block", padding: "0 16px 6px" }}>{field.label}</span>
                                            <PaymentMethodTextsEditor
                                                key={`${selectedKey}-${selectedData.layout}-${key}`}
                                                value={props[key] || {}}
                                                onChange={(next) => setProp(key, next)}
                                                selectedLanguage={selectedLanguage}
                                            />
                                        </div>
                                    );
                                }
                                return (
                                    <SchemaFieldControl
                                        key={key}
                                        field={field}
                                        props={props}
                                        selectedLanguage={selectedLanguage}
                                        selectedKey={selectedKey}
                                        selectedLayout={selectedData.layout}
                                        onSetProp={setProp}
                                        onSetTranslatableProp={setTranslatableProp}
                                    />
                                );
                            })}
                            {/* MEELZ's featuresGrid content (icon+label per item) is
                                edited here, appended below hero's own fields, since
                                featuresGrid isn't its own pickable component entry
                                anymore. Only shown for hero's "meelz" layout —
                                e4f/ck's equivalent field is "features", already
                                rendered by the schema loop above for those layouts. */}
                            {selectedKey === "hero" && selectedData.layout === "meelz" && COMPONENT_REGISTRY.featuresGrid && (() => {
                                const fgEntry = COMPONENT_REGISTRY.featuresGrid;
                                const fgData = propsMap.featuresGrid || buildDefaultEntry(fgEntry);
                                const fgProps = readEntryProps(fgEntry, fgData);
                                const fgItemsField = fgEntry.schema.find((f) => f.key === "items");
                                return fgItemsField ? (
                                    <div style={{ padding: "8px 0 4px" }}>
                                        <Divider style={{ margin: "4px 0 10px" }} />
                                        <span className={css.propLabel} style={{ display: "block", padding: "0 16px 6px" }}>
                                            {fgItemsField.label}
                                        </span>
                                        <CardsEditor
                                            key={`featuresGrid-${fgData.layout}`}
                                            cards={fgProps.items || []}
                                            onChange={setFeaturesGridItems}
                                            fields={fgItemsField.fields}
                                            newItemDefault={fgItemsField.newItemDefault}
                                            selectedLanguage={selectedLanguage}
                                            componentKey="featuresGrid"
                                        />
                                    </div>
                                ) : null;
                            })()}
                        </>
                    )}
                </div>
            </div>

            {/* Right: live preview */}
            <div className={css.previewPanel}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className={css.previewLabel} style={{ flex: 1 }}>Preview — {selected?.label}</div>
                    {Preview && (
                        <Segmented
                            value={previewFrameSize}
                            onChange={setPreviewFrameSize}
                            options={[
                                { value: "small", label: <Tooltip title="Mobile (375px)"><MobileOutlined /></Tooltip> },
                                { value: "medium", label: <Tooltip title="Tablet (768px)"><TabletOutlined /></Tooltip> },
                                { value: "large", label: <Tooltip title="Laptop (1280px)"><LaptopOutlined /></Tooltip> },
                            ]}
                            size="small"
                        />
                    )}
                    <Popconfirm
                        title="Reset this component to defaults?"
                        description="Discards this component's unpublished local changes — the live published site is unaffected until you Publish again."
                        onConfirm={resetSelectedToDefault}
                        okText="Reset"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                    >
                        <Button size="small">Reset</Button>
                    </Popconfirm>
                    <Select
                        value={selectedLanguage}
                        onChange={setSelectedLanguage}
                        options={LANGUAGE_SELECT_OPTIONS}
                        size="small"
                        style={{ width: 110 }}
                    />
                    {selected?.layouts && (
                        <Switch
                            checked={!!selectedData.active}
                            onChange={setActive}
                            size="small"
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                        />
                    )}
                    {/* Whole-section show/hide on the live client site — distinct
                        from Active/Inactive above (published-layout vs. fallback
                        content, never hides anything). Defaults to shown when
                        `visible` is unset (older saved entries, or this session's
                        still-unsaved draft before Publish), matching the `!== false`
                        check the client itself uses. Excluded for thankYou: it's a
                        full post-checkout route, not an embeddable page-section —
                        there's no client-side wrapper that consumes `visible` for
                        it, so hiding it here would do nothing but confuse admins. */}
                    {selected?.layouts && selectedKey !== "thankYou" && (
                        <Switch
                            checked={selectedData.visible !== false}
                            onChange={setVisible}
                            size="small"
                            checkedChildren="Shown"
                            unCheckedChildren="Hidden"
                        />
                    )}
                </div>
                <div className={css.previewCard} style={{ flexDirection: "column", width: "100%" }}>
                    {/* Remounts (resetting any interactive preview state, e.g.
                        StructuralFAQPreview's activeTab/openItems) whenever the
                        selected component or layout changes — otherwise
                        switching FAQ's own layout (tabbed → accordion → grid)
                        carries over a stale open-tab/open-item index into a
                        completely different items list. */}
                    <div style={{ width: PREVIEW_FRAME_WIDTH_BY_SIZE[previewFrameSize] || PREVIEW_FRAME_WIDTH_BY_SIZE.large, maxWidth: "100%", margin: "0 auto" }}>
                        {Preview && (
                            <div style={{ fontSize: 11, color: "#999", textAlign: "center", marginBottom: 6 }}>
                                {PREVIEW_FRAME_DEVICE_LABEL[previewFrameSize] || PREVIEW_FRAME_DEVICE_LABEL.large} — {PREVIEW_FRAME_WIDTH_BY_SIZE[previewFrameSize] || PREVIEW_FRAME_WIDTH_BY_SIZE.large}px
                            </div>
                        )}
                        {/* previewSize is only read by StructuralNavbarPreview
                            (hides its nav links at "small", matching the real
                            `hidden md:flex` breakpoint) — every other Preview
                            component ignores this extra prop, same as they
                            already ignore any prop shape differences between
                            component types. */}
                        {Preview && <Preview key={`${selectedKey}-${selectedData.layout}`} p={props} lang={selectedLanguage} previewSize={previewFrameSize} />}
                    </div>
                    {/* MEELZ's "meelz" hero LAYOUT only (not this admin build's
                        brand — an admin can preview any brand's hero layout
                        regardless of which brand this dashboard is built for,
                        so this must check the currently-selected layout, not
                        the build-time brandCode constant, or it wrongly shows
                        under E4F/CK layouts too). The real live MEELZ site
                        renders featuresGrid as its own always-visible section
                        immediately BELOW the whole hero block (not nested
                        inside it, unlike E4F/CK's real design — see
                        HERO_MEELZ_HIDDEN_FIELDS' comment). Full-width, stacked
                        under the hero preview card — not a sibling in the same
                        flex row (previewCard is a flex row by default), and
                        not part of hero's own schema/data, purely a preview
                        composition so admins editing MEELZ's hero see the same
                        stacked picture the live site shows. */}
                    {selectedKey === "hero" && selectedData.layout === "meelz" && COMPONENT_REGISTRY.featuresGrid && (() => {
                        const fgEntry = COMPONENT_REGISTRY.featuresGrid;
                        const fgData = propsMap.featuresGrid || {};
                        const fgProps = readEntryProps(fgEntry, fgData);
                        const FgPreview = fgEntry.Preview;
                        return FgPreview ? (
                            <div style={{ marginTop: 16, width: "100%" }}>
                                <FgPreview key={`featuresGrid-${fgData.layout}`} p={fgProps} lang={selectedLanguage} />
                            </div>
                        ) : null;
                    })()}
                </div>
            </div>
        </div>
    );
}

export { ClientBuilderPanel };
